import { FC, useMemo, useState, useEffect, useRef, Key, } from "react";
// 组件
import SelectInput, { SelectInputActionType } from "./subpage/selectInput";
import RenderLabel from "./subpage/renderLabel";
import SelectDropdown from "./subpage/selectDropdown";
// 方法
import classNames from "classnames";
import useClickOutside from "../Hooks/useClickOutside";
import { useLayoutContext } from '../PageLayout/context/LayoutContext';
import { getElementScrollLeftAndTop, getElementTopAndLeft } from "../utils/utils";
import { getOptionLabel, getSelectOptions, flatArray } from "./util";
// 常量
import { DEFAULT_FIELD_NAMES, DropdownOffset, SelectDropdownActionType, SelectOption, SelectValue, SuperSelectProps } from "./interface";
import { TABLE_COLUMN_EMPTY } from "../constants/constant";
import './style.less';

/** 下拉列表 */
const SuperSelect: FC<SuperSelectProps> = props => {
  const {
    value: outValue, options: outOptions, style, className, searchPlaceholder, isDisabledEllipsis, searchDiscriminateCase,
    size, placeholder, showSearch, mode, enumCode, loading, defaultOpen, defaultValue, allowClear, disabled, actionRef,
    popupClassName, dropdownStyle, dropdownMatchSelectWidth, listHeight, dropdownFooterRender, fieldNames, isLocalSearch,
    isUpperCase, disabledBorderDashed, showAllSelected, disabledRender, zIndex, copy, routeLinkProps,
    onRenderLabel, onClear, filterOption, onSearch, onBlur, onFocus, onChange, onSelect, getPopupContainer,
    renderOption, onSelectedAll,
  } = props;
  const [value, setValue] = useState<SelectValue | undefined>(defaultValue); // 下拉框选中的值
  const [options, setOptions] = useState<SelectOption[]>(); // 未过滤的选项
  const [isFocus, setIsFocus] = useState<boolean>(false); // 下拉框是否获取焦点
  const selectInputRef = useRef<SelectInputActionType>(); // input 输入框
  const selectDropdownRef = useRef<SelectDropdownActionType>(); // dropdown
  const flatOptions = useRef<SelectOption[]>(); // 拉平选项
  const selectRef = useRef<HTMLDivElement>(null); // 下拉框节点
  const dropdownRef = useRef<HTMLDivElement>(null); // dropdown节点
  const dropdownOffset = useRef<DropdownOffset>(); // dropdown定位数据
  const isFirst = useRef<boolean>(false); // 是否已经打开过下拉框
  const isChange = useRef<boolean>(false); // 是否触发onChange回调
  const dropdownWidth = useRef<number>(); // dropdown宽度
  const { enumData } = useLayoutContext();
  useClickOutside([selectRef, dropdownRef], isFocus, () => {
    setIsFocus(false);
    onBlur?.();
  });
  useEffect(() => {
    if (defaultOpen) {
      setTimeout(() => {
        isFirst.current = true;
        handleSetOffset();
        setIsFocus(true);
        onFocus?.();
      }, 200);
    }
  }, []);
  useEffect(() => {
    if (outOptions) {
      flatOptions.current = flatArray(outOptions, fieldNames?.children || DEFAULT_FIELD_NAMES.children);
      setOptions(outOptions);
    }
    if (enumCode && enumData && enumData[enumCode]) {
      const enumOptions = (enumData[enumCode].items || []) as SelectOption[];
      flatOptions.current = flatArray(enumOptions, fieldNames?.children || DEFAULT_FIELD_NAMES.children);
      setOptions(enumOptions);
    }
  }, [outOptions, enumCode]);
  useEffect(() => {
    isChange.current = false;
    const outValueType: boolean = Array.isArray(outValue);
    if (mode && !outValueType) {
      setValue(undefined);
      return;
    }
    if (!mode && outValueType) {
      setValue(undefined);
      return;
    }
    if (outValue === undefined || outValue === null) {
      setValue(defaultValue);
      return;
    }
    setValue(outValue);
  }, [outValue]);
  useEffect(() => {
    isChange.current && onChange?.(value, getSelectOptions(flatOptions.current, value, mode, fieldNames));
  }, [value]);
  /** 获取下拉框定位 */
  const handleSetOffset = () => {
    const selectElement = selectRef.current;
    const popupContainerOffset: DropdownOffset = {left: 0, top: 0};
    if (getPopupContainer) {
      const popupContainer: HTMLElement = getPopupContainer();
      const popupOffset = getElementTopAndLeft(popupContainer);
      popupContainerOffset.left = popupOffset.left;
      popupContainerOffset.top = popupOffset.top;
    }
    if (selectElement) {
      const selectHeight = selectElement.clientHeight;
      const selectWidth = selectElement.clientWidth;
      const selectOffset = getElementTopAndLeft(selectElement);
      const selectScroll = getElementScrollLeftAndTop(selectElement);
      const pageHeight = document.body.scrollHeight;
      const pageWidth = document.body.scrollWidth;
      dropdownWidth.current = Math.max(selectElement.clientWidth, dropdownMatchSelectWidth || 0);
      let dropdownLeft = selectOffset.left - popupContainerOffset.left - selectScroll.left;
      let dropdownTop = selectOffset.top + selectHeight - popupContainerOffset.top - selectScroll.top;
      let dropDownBottom = 0;
      if ((dropdownLeft + dropdownWidth.current) > pageWidth) {
        dropdownLeft = dropdownLeft - dropdownWidth.current + selectWidth;
      }
      if ((dropdownTop + (listHeight || 300)) > pageHeight) {
        dropDownBottom = pageHeight - dropdownTop + selectHeight;
        dropdownTop = 0;
      }
      dropdownOffset.current = {
        left: dropdownLeft,
        top: dropdownTop,
        bottom: dropDownBottom,
      };
    }
  };
  /** 点击下拉框 */
  const handleClickSelect = () => {
    if (disabled) return;
    handleSetOffset();
    setIsFocus(prev => {
      const newPrev = !prev;
      if (newPrev && !isFirst.current) {
        isFirst.current = true;
      }
      newPrev ? onFocus?.() : onBlur?.();
      return newPrev;
    });
    // onFocus?.()
  };
  /** 清空搜索框的值 */
  const handleEmptySearch = () => {
    selectDropdownRef.current?.onEmptySearch();
  };
  /** 点击选项 */
  const handleClickItem = (optionValue: Key | boolean, option: SelectOption) => {
    isChange.current = true;
    if (mode === 'multiple' && typeof optionValue !== 'boolean') {
      const newValue = new Set(value as Key[]);
      if (newValue.has(optionValue)) {
        newValue.delete(optionValue);
      } else {
        newValue.add(optionValue);
      }
      const nextValue = newValue.size ? [...newValue] : undefined;
      setValue(nextValue);
      onSelect?.(nextValue, getSelectOptions(flatOptions.current, nextValue, mode, fieldNames));
      return;
    }
    if (mode === 'tags') {
      return;
    }
    if (value !== optionValue) {
      setValue(optionValue);
      onSelect?.(optionValue, getSelectOptions(flatOptions.current, optionValue, mode, fieldNames));
    }
    setIsFocus(false);
    onBlur?.();
  };
  /** 点击全选 */
  const handleSelectedAll = () => {
    if (mode === 'multiple') {
      isChange.current = true;
      const allValues = (options?.map(item => item.value)) as Key[];
      setValue(allValues);
      onSelectedAll?.(allValues, options);
    }
  };
  /** 清除选项 */
  const handleClear = () => {
    isChange.current = true;
    setValue(undefined);
    onClear?.();
  };
  /** 获取焦点 */
  const handleFocus = () => {
    setIsFocus(true);
  }
  /** 失去焦点 */
  const handleBlur = () => {
    setIsFocus(false);
  }
  /** 禁用显示 */
  const detailRender = () => {
    if (disabledRender) {
      const renderText = typeof disabledRender === 'function' ? disabledRender() : disabledRender;
      return (
        <RenderLabel
          text={renderText}
          copy={copy}
          routeLinkProps={routeLinkProps}
          isDisabledEllipsis={isDisabledEllipsis}
        />
      );
    }
    const disabledRenderNode  = getOptionLabel(flatOptions.current, value, fieldNames);
    if (Array.isArray(disabledRenderNode)) {
      const joinRender = disabledRenderNode.join('，');
      return (
        <RenderLabel
          text={joinRender}
          copy={copy}
          routeLinkProps={routeLinkProps}
          isDisabledEllipsis={isDisabledEllipsis}
        />
      );
    }
    if (disabledRenderNode) {
      return (
        <RenderLabel
          text={disabledRenderNode}
          copy={copy}
          routeLinkProps={routeLinkProps}
          isDisabledEllipsis={isDisabledEllipsis}
        />
      );
    }
    return TABLE_COLUMN_EMPTY;
  };
  const selectClasses = useMemo(() => {
    return classNames('yb-select', `yb-select-${size}`, className, {
      'yb-select-focus': isFocus,
      'yb-select-disabled': disabled,
    });
  }, [size, isFocus, disabled]);
  if (actionRef) {
    actionRef.current = {
      focus: handleFocus,
      blur: handleBlur,
      emptySearch: handleEmptySearch,
      getOption: () => options || [],
    };
  }

  return (
    <>
      {
        disabled ? (
          <div className={isDisabledEllipsis ? 'yb-line-ellipsis-1' : undefined} style={disabledBorderDashed ? { borderBottom: 'dashed 1px #adb5bd' } : undefined}>
            {detailRender()}
          </div>
        ) : (
          <>
            <div
              ref={selectRef}
              style={style}
              className={selectClasses}
              onClick={handleClickSelect}
            >
              <div
                className="yb-select-tags"
                onMouseLeave={selectInputRef.current?.hideInputClose}
                onMouseEnter={selectInputRef.current?.showInputClose}
              >
                <span className="yb-select-tags-item yb-line-ellipsis-1">
                  {
                    (value !== undefined && value !== null) && (
                      <span className="yb-select-tags-item-text">
                        {
                          Array.isArray(value) ? getOptionLabel(flatOptions.current, value[0], fieldNames, onRenderLabel) : getOptionLabel(flatOptions.current, value, fieldNames, onRenderLabel)
                        }
                      </span>
                    )
                  }
                </span>
                {
                  mode === 'multiple' && (
                    <span className="yb-select-tags-num">
                      {
                        Array.isArray(value) && value.length > 1 && (
                          <span className="yb-select-tags-num-text">+{value.length - 1}</span>
                        )
                      }
                    </span>
                  )
                }
              </div>
              <SelectInput
                actionRef={selectInputRef}
                value={value}
                placeholder={placeholder}
                isFocus={isFocus}
                loading={loading}
                disabled={disabled}
                allowClear={allowClear}
                onClear={handleClear}
              />
            </div>
            {
              isFirst.current && (
                <SelectDropdown
                  actionRef={selectDropdownRef}
                  show={isFocus}
                  zIndex={zIndex}
                  loading={loading}
                  dropdownRef={dropdownRef}
                  left={dropdownOffset.current?.left}
                  top={dropdownOffset.current?.top}
                  bottom={dropdownOffset.current?.bottom}
                  showSearch={showSearch}
                  options={options}
                  value={value}
                  mode={mode}
                  isLocalSearch={isLocalSearch}
                  fieldNames={fieldNames}
                  minWidth={dropdownWidth.current || 220}
                  popupClassName={popupClassName}
                  dropdownStyle={dropdownStyle}
                  listHeight={listHeight}
                  dropdownFooterRender={dropdownFooterRender}
                  searchPlaceholder={searchPlaceholder}
                  searchDiscriminateCase={searchDiscriminateCase}
                  isUpperCase={isUpperCase}
                  showAllSelected={showAllSelected}
                  renderOption={renderOption}
                  onClickItem={handleClickItem}
                  filterOption={filterOption}
                  onSearch={onSearch}
                  getPopupContainer={getPopupContainer}
                  onSelectedAll={handleSelectedAll}
                />
              )
            }
          </>
        )
      }
    </>
  );
};

SuperSelect.defaultProps = {
  size: 'small',
  showSearch: true,
  loading: false,
  allowClear: false,
  disabled: false,
  fieldNames: DEFAULT_FIELD_NAMES,
  isLocalSearch: true,
  searchPlaceholder: '搜索内容',
  placeholder: '请选择',
  listHeight: 300,
  isDisabledEllipsis: false,
  disabledBorderDashed: true,
  searchDiscriminateCase: false,
  isUpperCase: false,
};

export default SuperSelect;
